"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import koJson from "@/locales/ko.json";

type Language = "en" | "ko";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNested(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split(".");
  let current: unknown = obj;
  for (const k of keys) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[k];
  }
  return typeof current === "string" ? current : undefined;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("ko");
  /** 첫 SSR·hydration에서 t()가 키 문자열을 노출하지 않도록 ko를 동기 기본값으로 둠 */
  const [translations, setTranslations] = useState<Record<string, unknown>>(
    () => koJson as unknown as Record<string, unknown>
  );

  useEffect(() => {
    const saved = localStorage.getItem("game-language") as Language;
    if (saved === "en" || saved === "ko") setLanguageState(saved);
  }, []);

  useEffect(() => {
    import(`@/locales/${language}.json`).then((m) => setTranslations(m.default));
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("game-language", lang);
  };

  const t = (key: string): string => {
    const v = getNested(translations as Record<string, unknown>, key);
    return v ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
